import { memo, PropsWithChildren, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { css, Interpolation, Theme } from '@emotion/react';
import { useCalendarGridStore, useNonReactiveStore } from '../store/calendar-grid.store';
import { useNonReactiveZoomStore } from '../store/zoom.store';
import { CalendarEvent, CalendarGridDragStyles } from '../types';
import { getEffectiveDuration } from '../utils';
import { CalendarDragStateContext, defaultCalendarDragState } from './calendar-drag-state-context';
import { EventDragHandle, eventDragHandleContainerStyle } from './column-events/event-drag-handle';
import { EVENT_CSS_MAP, EventCardContent } from './column-events/event-renderer';

const MIN_DRAG_DISTANCE = 5;

/**
 * Provides a DndContext for event card drag-and-drop.
 * Drop target detection uses `document.elementsFromPoint` against `data-column-id` attributes
 * rather than @dnd-kit droppable registration, so it does not conflict with the nested
 * column-reorder DndContext inside ColumnGroupSections.
 */
export const EventDragDropContext = ({ children }: PropsWithChildren) => {
  const isEventDragEnabled = useCalendarGridStore((s) => s.isEventDragEnabled);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const nonReactiveStore = useNonReactiveStore();
  const nonReactiveZoomStore = useNonReactiveZoomStore();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: MIN_DRAG_DISTANCE } }));

  const handleDragStart = ({ active }: DragStartEvent) => {
    const activeId = active.id as string;
    setActiveDragId(activeId);
    nonReactiveStore.getState().onEventDragStartRef.current?.(activeId);
  };

  const handleDragEnd = ({ active }: DragEndEvent) => {
    setActiveDragId(null);
    nonReactiveStore.getState().setEventDragPreview(null);

    const { onEventDragEndRef, dragSnapMinutes } = nonReactiveStore.getState();
    const { pxPerMinute } = nonReactiveZoomStore.getState();
    if (!onEventDragEndRef.current) return;

    const translated = active.rect.current.translated;
    if (!translated) return;

    // Use the center of the dragged card to locate the drop column.
    // elementsFromPoint returns all elements top-to-bottom; the dragged card itself
    // has opacity: 0 but is still in the DOM, so we look past it with `find`.
    const cx = translated.left + translated.width / 2;
    const cy = translated.top + translated.height / 2;

    const elements = document.elementsFromPoint(cx, cy);
    const columnEl = elements.find((el) => (el as HTMLElement).dataset?.columnId) as HTMLElement | undefined;
    if (!columnEl) return;

    const { columnId, groupId } = columnEl.dataset as { columnId?: string; groupId?: string };
    if (!columnId || !groupId) return;

    const columnRect = columnEl.getBoundingClientRect();
    const relativeY = translated.top - columnRect.top;
    const rawMinutes = relativeY / pxPerMinute;
    const snap = dragSnapMinutes ?? 5;
    const snappedMinutes = Math.round(rawMinutes / snap) * snap;
    const clampedMinutes = Math.max(0, Math.min(snappedMinutes, 24 * 60 - 1));

    onEventDragEndRef.current(active.id as string, columnId, groupId, clampedMinutes);
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    nonReactiveStore.getState().setEventDragPreview(null);
  };

  const handleDragMove = ({ active }: DragMoveEvent) => {
    const translated = active.rect.current.translated;
    if (!translated) return;

    const cx = translated.left + translated.width / 2;
    const cy = translated.top;

    const elements = document.elementsFromPoint(cx, cy);
    const columnEl = elements.find((el) => (el as HTMLElement).dataset?.columnId) as HTMLElement | undefined;
    if (!columnEl) {
      nonReactiveStore.getState().setEventDragPreview(null);
      return;
    }

    const { columnId, groupId } = columnEl.dataset as { columnId?: string; groupId?: string };
    if (!columnId || !groupId) return;

    const { dragSnapMinutes } = nonReactiveStore.getState();
    const { pxPerMinute } = nonReactiveZoomStore.getState();
    const columnRect = columnEl.getBoundingClientRect();
    const relativeY = translated.top - columnRect.top;
    const rawMinutes = relativeY / pxPerMinute;
    const snap = dragSnapMinutes ?? 5;
    const snappedMinutes = Math.round(rawMinutes / snap) * snap;
    const startMinutes = Math.max(0, Math.min(snappedMinutes, 24 * 60 - 1));

    nonReactiveStore.getState().setEventDragPreview({ columnId, groupId, startMinutes, eventId: active.id as string });
  };

  if (!isEventDragEnabled) return <>{children}</>;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{ layoutShiftCompensation: false }}
    >
      {children}
      {createPortal(
        // z-index must exceed EVENT_Z_INDICES max (60) and CALENDAR_Z_INDICES max (100)
        <DragOverlay style={{ zIndex: 200 }}>
          {activeDragId && <EventDragOverlayContent eventId={activeDragId} />}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

const EventDragOverlayContent = ({ eventId }: { eventId: string }) => {
  const eventInfo = useCalendarGridStore((s) => s.eventsById[eventId]);
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);

  if (!eventInfo) return null;

  const resolvedEventDragOverlay =
    typeof dragStyles.eventDragOverlay === 'function'
      ? (dragStyles.eventDragOverlay as (e: typeof eventInfo) => Interpolation<Theme>)(eventInfo)
      : dragStyles.eventDragOverlay;

  const eventDragOverlayStyle: Interpolation<Theme> = [
    { opacity: 0.85, pointerEvents: 'none' },
    overlayContentStyle,
    defaultEventDragOverlayStyle,
    resolvedEventDragOverlay,
  ];

  return (
    <div css={[placeholderStyle, dragOverlayWrapperStyle, eventDragHandleContainerStyle(true)]}>
      <CalendarDragStateContext.Provider
        value={{ ...defaultCalendarDragState, isEventDragOverlay: true, eventDragOverlayStyle }}
      >
        <EventDragHandle css={eventDragOverlayStyle} />
        <EventCardContent eventInfo={eventInfo} />
      </CalendarDragStateContext.Provider>
    </div>
  );
};

interface EventDropOverlayContentProps {
  isOver: boolean;
  columnId: string;
  eventDragPreview: { columnId: string; startMinutes: number; eventId: string } | null;
  eventsById: Record<string, CalendarEvent | undefined>;
  dragStyles: CalendarGridDragStyles;
  pxPerMinute: number;
}

const EventDropOverlayContentComponent = ({
  isOver,
  columnId,
  eventDragPreview,
  eventsById,
  dragStyles,
  pxPerMinute,
}: EventDropOverlayContentProps) => {
  if (!isOver || eventDragPreview?.columnId !== columnId || !dragStyles.eventDropOverlay) return null;

  const draggedEvent = eventsById[eventDragPreview.eventId];
  if (!draggedEvent) return null;

  const eventCss = EVENT_CSS_MAP[draggedEvent.eventType];

  const overlayStyle =
    typeof dragStyles.eventDropOverlay === 'function'
      ? (dragStyles.eventDropOverlay as (e: CalendarEvent) => Interpolation<Theme>)(draggedEvent)
      : dragStyles.eventDropOverlay;

  if (!overlayStyle) return null;

  return (
    <div
      css={[
        placeholderStyle,
        eventCss,
        {
          top: eventDragPreview.startMinutes * pxPerMinute,
          height: getEffectiveDuration(draggedEvent?.duration) * pxPerMinute,
        },
      ]}
    >
      <CalendarDragStateContext.Provider
        value={{
          ...defaultCalendarDragState,
          isEventDropOverlay: true,
          eventDropOverlayStyle: overlayStyle,
        }}
      >
        <EventCardContent eventInfo={draggedEvent} />
      </CalendarDragStateContext.Provider>
    </div>
  );
};

export const EventDropOverlayContent = memo(EventDropOverlayContentComponent);

const overlayContentStyle = css({
  width: '100%',
  height: '100%',
  '> *': {
    height: '100%',
  },
});

const defaultEventDragOverlayStyle = css({
  transform: 'translateX(-15px) rotate(-10deg)',
  transition: 'transform 0.2s ease-out',
});

const placeholderStyle = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
});

const dragOverlayWrapperStyle = css({
  cursor: 'grabbing',
});
